/**
 * RequestQueueManager - Manages concurrent request limits
 *
 * This singleton class ensures that only a specified number of requests
 * can be active at any given time. Additional requests are queued and
 * processed when slots become available.
 */

class RequestQueueManager {
  constructor(maxConcurrent = 2) {
    this.maxConcurrent = maxConcurrent;
    this.activeRequests = 0;
    this.queue = [];
    this.completedRequests = 0;
    this.totalRequests = 0;
    this.progressCallbacks = [];
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Function that executes the request and returns a Promise
   * @returns {Promise} - Promise that resolves when the request completes
   */
  enqueue(requestFn) {
    this.totalRequests++;
    this.notifyProgress();

    return new Promise((resolve, reject) => {
      const queuedRequest = {
        requestFn,
        resolve,
        reject
      };

      this.queue.push(queuedRequest);
      this.processQueue();
    });
  }

  /**
   * Process the next request in the queue if capacity is available
   */
  processQueue() {
    // Check if we have capacity and pending requests
    if (this.activeRequests >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    // Get the next request from the queue
    const queuedRequest = this.queue.shift();
    this.activeRequests++;

    // Execute the request
    queuedRequest.requestFn()
      .then((result) => {
        this.activeRequests--;
        this.completedRequests++;
        this.notifyProgress();
        queuedRequest.resolve(result);
        this.processQueue(); // Process next request
      })
      .catch((error) => {
        this.activeRequests--;
        this.completedRequests++;
        this.notifyProgress();
        queuedRequest.reject(error);
        this.processQueue(); // Process next request even on error
      });
  }

  /**
   * Get current queue status (useful for debugging)
   */
  getStatus() {
    return {
      activeRequests: this.activeRequests,
      queuedRequests: this.queue.length,
      maxConcurrent: this.maxConcurrent,
      completedRequests: this.completedRequests,
      totalRequests: this.totalRequests
    };
  }

  /**
   * Register a callback to be notified of progress updates
   * @param {Function} callback - Function that receives progress info {completed, total, percentage}
   * @returns {Function} - Function to remove this callback
   */
  onProgress(callback) {
    this.progressCallbacks.push(callback);
    // Immediately notify with current progress
    this.notifyProgress();

    // Return unsubscribe function
    return () => this.removeProgressCallback(callback);
  }

  /**
   * Remove a progress callback
   * @param {Function} callback - The callback to remove
   */
  removeProgressCallback(callback) {
    this.progressCallbacks = this.progressCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Notify all progress callbacks with current progress
   */
  notifyProgress() {
    const progressInfo = {
      completed: this.completedRequests,
      total: this.totalRequests,
      percentage: this.totalRequests > 0 ? Math.round((this.completedRequests / this.totalRequests) * 100) : 0
    };

    this.progressCallbacks.forEach(callback => {
      try {
        callback(progressInfo);
      } catch (error) {
        console.error('Error in progress callback:', error);
      }
    });
  }

  /**
   * Reset progress tracking
   */
  reset() {
    this.completedRequests = 0;
    this.totalRequests = 0;
    this.notifyProgress();
  }

  /**
   * Clear all pending requests and reset progress
   */
  clear() {
    this.queue = [];
    this.reset();
  }
}

// Export the class as default
export default RequestQueueManager;

// Export a default singleton instance for general use
export const defaultRequestQueueManager = new RequestQueueManager(3);
